import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md transition-all duration-300">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-background-50 font-heading text-lg font-bold">Y</span>
            </div>
            <span className="font-heading text-foreground-950 text-lg font-semibold whitespace-nowrap">yoybuyfinds.lat</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-foreground-700 text-sm font-medium hover:text-foreground-950 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>
            <a href="#categories" className="text-foreground-700 text-sm font-medium hover:text-foreground-950 transition-colors whitespace-nowrap">
              {t('nav.categories')}
            </a>
            <a href="#why" className="text-foreground-700 text-sm font-medium hover:text-foreground-950 transition-colors whitespace-nowrap">
              {t('nav.about')}
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-background-300/60 bg-background-50 text-foreground-700 text-sm font-medium hover:border-foreground-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>{t('nav.select_category')}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl text-foreground-950 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-white z-40">
          <div className="flex flex-col p-6 gap-4">
            <Link
              to="/"
              className="text-foreground-700 text-base font-medium hover:text-foreground-950 transition-colors py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <a href="#categories" className="text-foreground-700 text-base font-medium hover:text-foreground-950 transition-colors py-2" onClick={() => setIsMobileMenuOpen(false)}>
              {t('nav.categories')}
            </a>
            <a href="#why" className="text-foreground-700 text-base font-medium hover:text-foreground-950 transition-colors py-2" onClick={() => setIsMobileMenuOpen(false)}>
              {t('nav.about')}
            </a>
            <p className="text-foreground-500 text-sm font-medium pt-2">{t('nav.select_category')}:</p>
            <div className="flex flex-col gap-1 max-h-[50vh] overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(cat.nameKey)}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}